import React, { useEffect, useRef, useState } from 'react';
import { formatActivationCode } from '../utils/activationCode';
import { Link } from 'react-router-dom';
import { formatDateTime } from '../utils/dateUtils';
import Layout from '../components/ui/Layout';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { PermissionGuard } from '../components/auth/PermissionGuard';
import {
  RiFileListLine,
  RiCustomerServiceLine,
  RiDeviceLine,
  RiAlertLine,
  RiCalendarTodoLine,
  RiMoneyDollarBoxLine,
  RiLineChartLine,
  RiPieChartLine,
  RiTimeLine,
  RiArrowRightSLine,
  RiArrowUpLine,
  RiArrowDownLine,
  RiRefreshLine,
  RiContractLine,
  RiToolsLine,
  RiDashboardLine
} from 'react-icons/ri';
import { 
  DashboardMetrics, 
  TopClient, 
  ExpiringContract, 
  RecentActivity,
  ExpiringRental
} from '../services/dashboardService';
import { showAlert } from '../utils/alert';
import EmptyState from '../components/ui/EmptyState';
import RentalTrendChart from '../components/charts/RentalTrendChart';
import DeviceUtilizationChart from '../components/charts/DeviceUtilizationChart';
import { 
  StatCardSkeleton, 
  ChartSkeleton, 
  TableSkeleton, 
  ActivitySkeleton 
} from '../components/ui/SkeletonLoader';
import { RefreshButton } from '../components/ui/LoadingButton';
import { 
  useDashboardSummary, 
  useDashboardCharts, 
  useDashboardTrends,
  usePrefetch 
} from '../hooks/useQueryHooks';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import dashboardService from '../services/dashboardService';
import preparationJobService, { customerDisplay } from '../services/preparationJobService';
import { queryKeys } from '../queryClient';
import { daysRemainingLabel } from '../utils/rentalExpiry';

/**
 * ตัวเลขเปรียบเทียบเดือนนี้ vs เดือนก่อนของการ์ด KPI
 *
 * P-SA69-1: เดิมการ์ดรับแค่ `percentChange` ตัวเลขลอยๆ แล้วเอาไปแปะบนยอดสะสมได้เลย
 * (เช่น tile "บริษัททั้งหมด 106" ติด ▼100% เพราะเดือนนี้ไม่มีลูกค้าใหม่ ทั้งที่ยอดรวม
 * ไม่ได้ลดสักราย) การ์ดจึงต้องรู้ทั้ง "เทียบอะไร" และ "ฐานเป็นเท่าไร" ถึงจะแสดงผลตรงความจริงได้
 */
interface StatDelta {
  /** ชื่อ metric ที่ delta ตัวนี้อ้างถึงจริงๆ เช่น 'ลูกค้าใหม่' (ไม่ใช่ยอดสะสมบนการ์ด) */
  label: string;
  /** ค่าของ metric นั้นในเดือนนี้ */
  current: number;
  /** ค่าเดียวกันของเดือนก่อน — ใช้เป็นฐาน % ถ้าเป็น 0 แปลว่าคำนวณ % ไม่ได้ */
  previous: number;
  /** ตัวจัดรูปแบบตัวเลขใน caption (default: toLocaleString) */
  format?: (value: number) => string;
}

// P-SA69-1: ตรรกะ badge อยู่ที่ utils/deltaBadge (ใช้ร่วมกับ /reports/dashboard) · re-export ให้เทสต์เดิม
export { computeDeltaBadge } from '../utils/deltaBadge';
export type { DeltaBadgeTone } from '../utils/deltaBadge';
import { computeDeltaBadge, deltaBadgeSpeech } from '../utils/deltaBadge';

// คอมโพเนนต์แสดงการ์ดข้อมูลสรุป
interface StatCardProps {
  title: string;
  value: number | string;
  delta?: StatDelta;
  icon: React.ReactNode;
  bgColor: string;
  textColor: string;
  iconColor: string;
  link: string;
  /** ตัวเลข/ข้อความรองใต้ชื่อการ์ด — ใช้เมื่อการ์ดสื่อ 2 ความหมาย (เช่น สถานะ vs ใบงานค้าง) */
  note?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  delta,
  icon,
  bgColor,
  textColor,
  iconColor,
  link,
  note
}) => {
  const deltaBadge = delta ? computeDeltaBadge(delta) : null;
  const fmt = delta?.format ?? ((v: number) => v.toLocaleString());
  const valueText = typeof value === 'number' && value >= 1000 ? value.toLocaleString() : value;
  // aria-label ของลิงก์ "แทนที่" ข้อความข้างในทั้งหมด — เดิมมีแค่ "{title}: {value}" ผู้ใช้โปรแกรมอ่านหน้าจอ
  // ไม่ได้ยิน note (รวมสูญหาย/ใบซ่อมค้าง) · caption เทียบเดือน · ทิศทาง badge (KD3 S2464 NFR-DASH-004)
  const deltaSpeech = deltaBadge ? deltaBadgeSpeech(deltaBadge) : '';
  const ariaLabel = [
    `${title}: ${valueText}`,
    note,
    delta && `${delta.label} ${fmt(delta.current)} เดือนนี้ · ${fmt(delta.previous)} เดือนก่อน${deltaSpeech ? ` (${deltaSpeech})` : ''}`,
  ].filter(Boolean).join(' · ');
  // ตรวจสอบประเภทของ card จาก iconColor เพื่อกำหนดสีที่เหมาะสม
  const getGradientClass = () => {
    if (iconColor.includes('success')) return 'hover:shadow-success-100';
    if (iconColor.includes('warning')) return 'hover:shadow-warning-100';
    if (iconColor.includes('info')) return 'hover:shadow-info-100';
    if (iconColor.includes('accent')) return 'hover:shadow-accent-100';
    return 'hover:shadow-secondary-100';
  };

  return (
    <Link
      to={link}
      aria-label={ariaLabel}
      className={`
        ${bgColor}
        p-4 rounded-xl shadow-card
        hover:shadow-card-hover transition-all duration-200
        border border-black/5
        group relative overflow-hidden
        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
      `}
    >
      {/* Subtle hover overlay */}
      <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors duration-200 rounded-xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-start justify-between mb-3">
          <div className={`
            p-2 rounded-lg ${iconColor}
            group-hover:scale-105 transition-transform duration-200
          `}>
            <div className="w-5 h-5">
              {icon}
            </div>
          </div>
          {deltaBadge && delta && (
            <div
              // tooltip บอกชัดว่าตัวเลขนี้เทียบอะไร — badge อยู่บนการ์ดยอดสะสม ไม่ได้พูดถึงยอดนั้น
              title={`${delta.label}: ${fmt(delta.current)} เดือนนี้ เทียบกับ ${fmt(delta.previous)} เดือนก่อน`}
              className={`
                flex items-center text-xs font-semibold
                px-2 py-1 rounded-lg
                ${deltaBadge.tone === 'up' ? 'text-success-700 bg-success-100' : ''}
                ${deltaBadge.tone === 'down' ? 'text-danger-700 bg-danger-100' : ''}
                ${deltaBadge.tone === 'neutral' ? 'text-secondary-600 bg-secondary-100' : ''}
              `}
            >
              {deltaBadge.tone === 'up' && <RiArrowUpLine className="mr-0.5 w-3 h-3" />}
              {deltaBadge.tone === 'down' && <RiArrowDownLine className="mr-0.5 w-3 h-3" />}
              {deltaBadge.text}
            </div>
          )}
        </div>
        <p className="text-2xl font-bold text-secondary-900 mb-0.5 tabular-nums">
          {typeof value === 'number' && value >= 1000 ? value.toLocaleString() : value}
        </p>
        <p className={`text-xs font-medium ${textColor}`}>{title}</p>
        {note && <p className="text-xs text-secondary-600 mt-1">{note}</p>}
        {/* caption บอกที่มาของ badge เสมอ — กันการอ่านว่า % นั้นเป็นการเปลี่ยนแปลงของยอดบนการ์ด */}
        {delta && (
          <p className="text-xs text-secondary-600 mt-1">
            {delta.label} {fmt(delta.current)} เดือนนี้ · {fmt(delta.previous)} เดือนก่อน
          </p>
        )}
      </div>
    </Link>
  );
};

// คอมโพเนนต์ตารางข้อมูล
interface DataTableProps {
  title: string;
  data: any[];
  columns: {key: string, label: string, format?: (value: any) => string | React.ReactElement}[];
  action?: {
    label: string;
    link: string;
  };
  /** KD3 S2459: query ล้ม (ยังไม่เคยได้ข้อมูล) — ห้ามโชว์ "ยังไม่มี…" เพราะผู้ใช้จะเข้าใจว่าไม่มีงานค้างจริง */
  loadError?: boolean;
}

const DataTable: React.FC<DataTableProps> = ({ title, data, columns, action, loadError }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-secondary-100 overflow-hidden hover:shadow-md transition-shadow duration-300">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-secondary-200 bg-gradient-to-r from-secondary-50/50 to-white">
        <h2 className="text-sm font-semibold text-secondary-800">{title}</h2>
        {action && (
          <Link
            to={action.link}
            // KD3 S2461 (NFR-DASH-001): จอมือถือพื้นที่กดเดิมสูง 18px → ขยายเป็น 44px (padding ไม่ขยับตัวอักษร)
            className="group inline-flex items-center min-h-[44px] sm:min-h-0 -my-3 sm:my-0 px-2 -mr-2 sm:px-0 sm:mr-0 text-xs font-medium text-primary-600 hover:text-primary-700 transition-all duration-200"
          >
            {action.label}
            <RiArrowRightSLine className="ml-0.5 w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform duration-200" />
          </Link>
        )}
      </div>
      <div className="overflow-x-auto" tabIndex={0} role="region" aria-label={`ตาราง${title}`}>
        {data.length > 0 ? (
          <table className="min-w-full divide-y divide-secondary-100" aria-label={title}>
            <thead className="bg-secondary-50/80 border-b border-secondary-200">
              <tr>
                {columns.map((column, index) => (
                  <th
                    key={index}
                    scope="col"
                    className="px-4 min-h-[44px] sm:min-h-[42px] py-2.5 text-left text-xs font-semibold text-secondary-500 tracking-normal whitespace-nowrap"
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-100">
              {data.map((row, rowIndex) => (
                <tr key={rowIndex} className="hover:bg-secondary-50/60 transition-colors duration-100">
                  {columns.map((column, colIndex) => {
                    const cell = column.format ? column.format(row[column.key]) : (row[column.key] || <span className="text-secondary-300">—</span>);
                    return (
                    <td key={colIndex} className={`px-4 py-3 whitespace-nowrap text-sm ${colIndex === 0 ? 'font-medium text-secondary-800' : 'text-secondary-600'}`}>
                      {/* BUG-S141-DASHTH FIX: คอลัมน์แรกเป็นข้อความยาว (ชื่อบริษัทไทย) + td มี
                          whitespace-nowrap → มันดันความกว้างตารางจนหัวคอลัมน์ที่เหลือถูกบีบ
                          เหลือ ~48px แล้ว label ไทยตัดบรรทัดทีละ 2 ตัวอักษร (หัวตารางสูง 129px
                          ที่ 1280px) · ตัดข้อความยาวทุกคอลัมน์ด้วย ellipsis + title ให้ดูเต็มได้
                          เมื่อ hover ทำให้ทุกคอลัมน์อยู่ในกรอบ widget พอดี */}
                      <span
                        className="block max-w-[13rem] truncate"
                        title={typeof cell === 'string' || typeof cell === 'number' ? String(cell) : undefined}
                      >
                        {cell}
                      </span>
                    </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        ) : loadError ? (
          <EmptyState
            icon={<RiFileListLine className="w-6 h-6" />}
            title="โหลดข้อมูลไม่สำเร็จ"
            description={`ดึงข้อมูล${title}ไม่ได้ — กดปุ่ม "รีเฟรช" ด้านบนเพื่อลองใหม่`}
            compact
          />
        ) : (
          <EmptyState
            icon={<RiFileListLine className="w-6 h-6" />}
            title="ไม่พบข้อมูล"
            description={`ยังไม่มี${title}`}
            compact
          />
        )}
      </div>
    </div>
  );
};

// KD3 S2484 (JOURNEY-MOVE-012): สิทธิ์ทุกตัวที่ widget บนแดชบอร์ดใช้ — role ที่ไม่มีสักตัว (เช่นมีแค่ dashboard.view +
// device_movements.view) เดิมได้หน้าเปล่ามีแต่หัวข้อกับปุ่มรีเฟรช ทั้งที่ login พาทุกคนมาหน้านี้ก่อน
// guard: src/pages/__tests__/s2484_dashboard_no_widget_empty_state.test.ts ตรวจว่าครอบทุก permission ในไฟล์
const DASHBOARD_WIDGET_PERMISSIONS = ['companies.view', 'contracts.view', 'devices.view', 'maintenance.view', 'preparation_jobs.view', 'rentals.view', 'reports.view'];

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { canCreate, canView, hasPermission } = usePermission();
  // FINDING-DASH-MAINT: การ์ด/ตาราง "ซ่อมบำรุง" นับจากใบงานซ่อมค้างแล้ว (ไม่ใช่แค่
  // device status) → ลิงก์ไปหน้ารายการซ่อมบำรุงถ้ามีสิทธิ์ ไม่งั้น fallback หน้าอุปกรณ์เดิม
  const maintenanceLink = canView('maintenance')
    ? '/maintenance/list'
    : '/master-data/devices?status=maintenance';
  // BUG-UI-05: การ์ดนี้ gate ด้วย devices.view (เป็นยอดอุปกรณ์) แต่ปลายทางต้องมี
  // preparation_jobs.view → role ที่มีแค่ devices.view คลิกแล้วเด้ง /access-denied
  // ใช้ pattern เดียวกับ maintenanceLink: มีสิทธิ์ก็ไปแท็บของค้างจอง ไม่มีก็ไปหน้าอุปกรณ์ที่เขาเปิดได้
  const reservedDevicesLink = canView('preparation_jobs')
    ? '/rentals/preparation-jobs?tab=reserved'
    : '/master-data/devices?status=reserved';
  const queryClient = useQueryClient();
  const { prefetchDashboard } = usePrefetch();
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  // ใช้ React Query hooks แยกตาม API endpoints
  const { 
    data: summaryData, 
    isLoading: isLoadingSummary, 
    error: summaryError,
    refetch: refetchSummary 
  } = useDashboardSummary();
  
  // Debug: ตรวจสอบ error (เฉพาะ development)
  if (summaryError && import.meta.env.DEV) {
    console.error('Dashboard Summary Error:', summaryError);
  }
  
  const { 
    data: chartsData, 
    isLoading: isLoadingCharts,
    error: chartsError,
    refetch: refetchCharts 
  } = useDashboardCharts();
  
  const { 
    data: trendsData, 
    isLoading: isLoadingTrends,
    error: trendsError,
    refetch: refetchTrends 
  } = useDashboardTrends();
  
  // ดึง widget configuration สำหรับ user ปัจจุบัน
  const {
    data: widgetConfig,
    isLoading: isLoadingWidgetConfig
  } = useQuery({
    queryKey: ['dashboard', 'my-widgets'],
    queryFn: () => dashboardService.getMyWidgets(),
    staleTime: 5 * 60 * 1000, // cache 5 นาที
    retry: 1 // ลองใหม่แค่ 1 ครั้งถ้า error
    // Note: onError removed in TanStack Query v5 — errors handled via meta or component-level effects
  });

  // Helper function: ตรวจสอบว่า widget ควรแสดงหรือไม่
  const isWidgetVisible = (widgetKey: string): boolean => {
    // ถ้า API error หรือ loading ให้แสดง widgets ทั้งหมด (default behavior)
    if (!widgetConfig) return true;

    // ถ้า all_widgets_visible = true ให้แสดงทั้งหมด
    if (widgetConfig.all_widgets_visible) return true;

    // เช็คว่า widget นี้มีใน config และ visible = true หรือไม่
    // ถ้า widget ไม่มีใน config หมายความว่ามันถูก unchecked ควร return false
    const widgetSetting = widgetConfig.widgets?.[widgetKey];
    return widgetSetting?.visible === true;
  };

  // Helper function: ดึง order ของ widget (ใช้ default order ถ้าไม่มีใน config)
  const getWidgetOrder = (widgetKey: string, defaultOrder: number): number => {
    if (!widgetConfig || widgetConfig.all_widgets_visible) return defaultOrder;
    const widgetSetting = widgetConfig.widgets?.[widgetKey];
    return widgetSetting?.order ?? defaultOrder;
  };

  // เรียกข้อมูลเพิ่มเติมสำหรับตาราง
  const {
    data: topCompaniesData,
    isLoading: isLoadingTopCompanies,
    isError: isTopCompaniesError
  } = useQuery({
    queryKey: ['dashboard', 'top-companies'],
    queryFn: () => dashboardService.getTopClients(5),
    refetchInterval: 30000 // รีเฟรชทุก 30 วินาที
  });
  
  const { 
    data: expiringContractsData,
    isLoading: isLoadingExpiringContracts,
    isError: isExpiringContractsError
  } = useQuery({
    queryKey: ['dashboard', 'expiring-contracts'],
    queryFn: () => dashboardService.getExpiringContracts(30, 5),
    refetchInterval: 30000
  });
  
  const { 
    data: recentActivitiesData,
    isLoading: isLoadingRecentActivities,
    isError: isRecentActivitiesError
  } = useQuery({
    queryKey: ['dashboard', 'recent-activities'],
    queryFn: () => dashboardService.getRecentActivities(10),
    enabled: canView('rentals') || canView('contracts'),
    refetchInterval: 30000
  });
  
  // ข้อมูลบริษัทที่ยังไม่มีสัญญา
  const { 
    data: companiesWithoutContractsData,
    isLoading: isLoadingCompaniesWithoutContracts,
    isError: isCompaniesWithoutContractsError
  } = useQuery({
    queryKey: ['dashboard', 'companies-without-contracts'],
    queryFn: () => dashboardService.getCompaniesWithoutContracts(5),
    refetchInterval: 60000 // รีเฟรชทุก 1 นาที
  });
  
  // ข้อมูลรายการเช่าที่ใกล้หมดอายุ
  const { 
    data: expiringRentalsData,
    isLoading: isLoadingExpiringRentals,
    isError: isExpiringRentalsError
  } = useQuery({
    queryKey: ['dashboard', 'expiring-rentals'],
    queryFn: () => dashboardService.getExpiringRentals(30, 5),
    refetchInterval: 60000 // รีเฟรชทุก 1 นาที
  });

  // ข้อมูลอุปกรณ์ที่ต้องซ่อม
  const {
    data: maintenanceDevicesData,
    isLoading: isLoadingMaintenanceDevices,
    isError: isMaintenanceDevicesError
  } = useQuery({
    queryKey: ['dashboard', 'devices-maintenance'],
    queryFn: () => dashboardService.getMaintenanceDevices(10),
    refetchInterval: 60000 // รีเฟรชทุก 1 นาที
  });

  // เคสซ่อมใหม่ (สถานะเปิด) — user feedback Suraluck 2026-08-24 ข้อ 2
  // backend gate ด้วย maintenance.view → ไม่มีสิทธิ์ก็ไม่ยิง (กัน 403 ทุกนาที)
  const {
    data: newMaintenanceCasesData,
    isError: isNewMaintenanceCasesError
  } = useQuery({
    queryKey: ['dashboard', 'new-maintenance-cases'],
    queryFn: () => dashboardService.getNewMaintenanceCases(10),
    enabled: canView('maintenance'),
    refetchInterval: 60000
  });

  // ใบงานเตรียมเครื่องที่ยังรอเตรียม (PO 2026-09-08)
  // gate ด้วย preparation_jobs.view → ไม่มีสิทธิ์ก็ไม่ยิง (กัน 403 ทุกนาที — pattern G-27)
  const {
    data: preparationJobsData,
    isError: isPreparationJobsError
  } = useQuery({
    queryKey: ['dashboard', 'preparation-jobs-requested'],
    queryFn: () => preparationJobService.getJobs({ status: 'REQUESTED', page_size: 5 }),
    enabled: canView('preparation_jobs'),
    retry: false,
    refetchInterval: 60000
  });

  // Prefetch data เมื่อ component mount
  useEffect(() => {
    prefetchDashboard();
  }, []);
  
  // รวม loading states
  const isLoading = isLoadingSummary || isLoadingCharts || isLoadingTrends ||
                    isLoadingTopCompanies || isLoadingExpiringContracts || isLoadingRecentActivities ||
                    isLoadingCompaniesWithoutContracts || isLoadingExpiringRentals || isLoadingMaintenanceDevices;
  const hasError = summaryError || chartsError || trendsError;
  
  // Extract data with fallbacks - ปรับให้ตรงกับ structure ใหม่ของ API
  const rental_stats = summaryData?.rental_stats || {};
  const company_stats = summaryData?.company_stats || {};
  const device_stats = summaryData?.device_stats || {};
  const contract_stats = summaryData?.contract_stats || {};
  const revenue_stats = summaryData?.revenue_stats || {};
  
  // Transform data to match existing component expectations
  const metrics = {
    totalRentals: rental_stats.total || 0,
    activeRentals: rental_stats.active || 0,
    pendingCancelRentals: rental_stats.pending_cancel || 0,
    completedRentals: rental_stats.completed || 0,
    totalCompanies: company_stats.total || 0,
    activeCompanies: company_stats.active || 0,
    newCompaniesThisMonth: company_stats.new_this_month || 0,
    // P-SA69-1: ฐานเทียบเดือนก่อน — ให้ StatCard แยก "ไม่มีของใหม่" ออกจาก "ไม่มีฐานให้คิด %"
    newCompaniesLastMonth: company_stats.new_last_month || 0,
    totalDevices: device_stats.total || 0,
    activeDevices: device_stats.rented || 0,
    availableDevices: device_stats.available || 0,
    // PO 2026-09-08: การ์ดซ่อมบำรุงต้องนับตาม `devices.status` เพื่อให้ผลรวมทุกการ์ด = ยอดรวม
    // (ของเดิม `maintenance` นับ "เครื่องที่มีใบซ่อมค้าง" ซึ่งซ้อนกับ available/rented → รวมเกิน)
    // `?? maintenance` ไว้กัน backend รุ่นก่อนที่ยังไม่ส่งคีย์ใหม่ (deploy skew)
    maintenanceDevices: device_stats.maintenance_by_status ?? device_stats.maintenance ?? 0,
    // ความหมายเดิมของการ์ด — โชว์เป็นตัวเลขรอง จะได้ไม่เสียข้อมูลที่การ์ดเดิมตั้งใจสื่อ
    openMaintenanceJobs: device_stats.open_maintenance_jobs ?? 0,
    damagedDevices: device_stats.damaged || 0,
    rentedDevices: device_stats.rented || 0,
    issuedDevices: device_stats.issued || 0,
    reservedDevices: device_stats.reserved || 0,
    // PO 2026-09-08: ไม่ทำการ์ดแยกให้ของที่ตายแล้ว (แดชบอร์ดยิ่งรกยิ่งไม่มีคนดู) แต่ต้องโชว์
    // เป็นตัวเลขรองบนการ์ด "อุปกรณ์ทั้งหมด" ไม่งั้นผู้ใช้บวกการ์ดบนจอแล้วไม่ครบยอดรวม
    lostDevices: device_stats.lost || 0,
    disposedDevices: device_stats.disposed || 0,
    monthlyRevenue: revenue_stats.this_month || 0,
    // FIXME: API ไม่มี field รายได้รวมทั้งปี (YTD) จริง — ค่านี้เป็นเพียงประมาณการ
    // จากรายได้เดือนปัจจุบัน * 12 ซึ่งจะไม่แม่นยำหากเดือนนี้มีรายได้ผิดปกติ
    // ควรเพิ่ม API endpoint ที่คำนวณ YTD revenue จากข้อมูลจริงทุกเดือน
    estimatedAnnualRevenue: (revenue_stats.this_month || 0) * 12,
    expiringContracts: contract_stats.expiring_soon || 0,
    totalContracts: contract_stats.total || 0,
    activeContracts: contract_stats.active || 0,
    deviceUtilizationRate: device_stats.utilization_rate || 0,
    newRentalsThisMonth: rental_stats.new_this_month || 0,
    newRentalsLastMonth: rental_stats.new_last_month || 0,
    previousMonthRevenue: revenue_stats.last_month || 0
  };
  
  const rentalTrends = Array.isArray(trendsData?.data) ? trendsData.data : [];
  const deviceUtilization = chartsData?.deviceUtilization || { status_distribution: [], device_type_distribution: [] };
  const topClients = topCompaniesData || [];
  const expiringContracts = expiringContractsData || [];
  const recentActivities = recentActivitiesData || [];
  const companiesWithoutContracts = companiesWithoutContractsData || [];
  const expiringRentals = expiringRentalsData || [];
  const maintenanceDevices = maintenanceDevicesData || [];
  // แถวของตาราง "เคสซ่อมใหม่": รวม id+เลขเคสไว้ใน field เดียวให้ column format render เป็นลิงก์เข้าเคสได้
  const newMaintenanceCases = (newMaintenanceCasesData || []).map((c) => ({
    ...c,
    case: { id: c.id, no: c.case_no },
    site_label: c.site_name || formatActivationCode(c.activation_code) || c.device_code || '—',
  }));
  // แถวของตาราง "ใบงานเตรียมเครื่อง (รอเตรียม)" — รวม id+เลขที่ใบไว้ใน field เดียวให้ render เป็นลิงก์ได้
  const preparationJobRows = (preparationJobsData?.items || []).map((job) => ({
    ...job,
    job: { id: job.id, no: job.job_number },
    customer_label: customerDisplay(job),
  }));
  
  // ฟังก์ชันรีเฟรชข้อมูล
  // KD3 S2462 (NEG-DASH-002): รัวกดรีเฟรช 10 ครั้ง = ยิง 11 endpoint × 10 รอบ + toast ซ้อน 10 อัน
  // (ปุ่มผูก loading กับ isLoading ซึ่งเป็น true แค่ตอนโหลดครั้งแรก) → กันกดซ้ำระหว่างรอบที่ยังไม่จบ
  const refreshingRef = useRef(false);
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    setRefreshing(true);
    try {
      // R5-④: invalidate ที่ "คำนำหน้า" ['dashboard'] ครั้งเดียว แทนการไล่ระบุทีละ key
      //
      // เดิมไล่ระบุ 9 บรรทัด แต่หน้านี้มี queryKey ขึ้นต้นด้วย 'dashboard' อยู่ 9 ตัว และ 2 ตัวที่เพิ่ม
      // ทีหลังสุด ('new-maintenance-cases' + 'preparation-jobs-requested') ไม่ได้ถูกเพิ่มเข้ารายการนี้
      // → ผู้ใช้กด "รีเฟรช" แล้วอีก 6 ส่วนอัปเดตทันที แต่ 2 กล่องนั้นค้างของเก่าจนครบรอบ 60 วิ
      // ของตัวเอง ซึ่งอ่านออกมาเหมือน "ปุ่มรีเฟรชเสีย"
      //
      // React Query จับคู่ queryKey แบบ prefix อยู่แล้ว → ['dashboard'] ครอบทุกคีย์ของหน้านี้
      // และ **ไม่มีทางหลุดอีกตอนเพิ่ม widget ใหม่** ซึ่งเป็นสาเหตุที่แท้จริงของบั๊กนี้
      // (queryKeys.dashboard.all = ['dashboard'] — queryClient.ts:94)
      showAlert('รีเฟรชข้อมูล', 'กำลังโหลดข้อมูลใหม่...', 'info');
      await queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });

      setLastRefresh(new Date());
    } finally {
      refreshingRef.current = false;
      setRefreshing(false);
    }
  };
  
  // ฟังก์ชันฟอร์แมตค่าเงิน
  const formatCurrency = (value: number) => {
    return value.toLocaleString('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };
  
  // ฟังก์ชันฟอร์แมตวันที่
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      day: 'numeric',
      month: 'short',
      year: '2-digit'
    });
  };
  
  // ฟังก์ชันฟอร์แมตจำนวนวันที่เหลือ
  // BUG-S2338-001: ครบกำหนดวันนี้ (0) ≠ หมดอายุแล้ว · ค่าว่าง ≠ 0 — กติกาอยู่ที่ utils/rentalExpiry.daysRemainingLabel
  const formatDaysRemaining = (days: number | null | undefined) => {
    const { text, tone } = daysRemainingLabel(days);
    const cls = {
      danger: 'text-danger-600 font-semibold',
      warning: 'text-warning-600 font-semibold',
      success: 'text-success-600',
      muted: 'text-gray-400',
    }[tone];
    return <span className={cls}>{text}</span>;
  };
  
  // ฟังก์ชันฟอร์แมตกิจกรรม
  const formatActivity = (activity: string) => {
    const activityMap: { [key: string]: string } = {
      'rental_created': 'สร้างการเช่าใหม่',
      'rental_updated': 'อัพเดทข้อมูลการเช่า',
      'rental_cancelled': 'ยกเลิกการเช่า',
      'contract_created': 'สร้างสัญญาใหม่',
      'contract_signed': 'ลงนามสัญญา',
      'device_assigned': 'จัดสรรอุปกรณ์',
      'device_returned': 'คืนอุปกรณ์',
      'payment_received': 'รับชำระเงิน',
      'maintenance_scheduled': 'นัดหมายซ่อมบำรุง'
    };
    return activityMap[activity] || activity;
  };
  
  // แสดง loading skeleton
  if (isLoading) {
    return (
      <Layout>
        <div>
          {/* OBS-S1148-001 FIX: match loaded Header layout (lines 523-543) to prevent CLS */}
          {/* Use same `flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6` as loaded */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-secondary-200 animate-pulse flex-shrink-0" />
              <div>
                <div className="h-5 w-24 bg-secondary-200 rounded-lg animate-pulse mb-1.5" />
                <div className="h-3 w-48 bg-secondary-100 rounded animate-pulse" />
              </div>
            </div>
            <div className="h-8 w-24 bg-secondary-100 rounded-lg animate-pulse" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, index) => (
              <StatCardSkeleton key={index} />
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, index) => (
              <StatCardSkeleton key={index + 4} />
            ))}
          </div>

          {/* OBS-S1148-001 FIX: charts wrapped in card-free h-64 div matching loaded RentalTrendChart/DeviceUtilizationChart shape */}
          {/* (ChartSkeleton has card+padding=~84px extra height → caused CLS shift on skeleton→loaded transition) */}
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-4 h-4 bg-secondary-200 rounded animate-pulse flex-shrink-0" />
              <div className="h-4 w-32 bg-secondary-200 rounded animate-pulse" />
              <div className="flex-1 h-px bg-secondary-200" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="h-64 bg-secondary-100 rounded-lg animate-pulse" role="region" aria-label="กำลังโหลดกราฟ" />
              <div className="h-64 bg-secondary-100 rounded-lg animate-pulse" role="region" aria-label="กำลังโหลดกราฟ" />
            </div>
          </div>

          {/* OBS-S1148-001 FIX: add Tables section header placeholder + use 2-col grid + gap-6 to match loaded layout */}
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-4 h-4 bg-secondary-200 rounded animate-pulse flex-shrink-0" />
              <div className="h-4 w-40 bg-secondary-200 rounded animate-pulse" />
              <div className="flex-1 h-px bg-secondary-200" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <TableSkeleton />
              <TableSkeleton />
            </div>
          </div>
        </div>
      </Layout>
    );
  }
  
  // แสดง error state
  if (hasError) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <EmptyState
            icon={<RiAlertLine className="w-16 h-16" />}
            title="เกิดข้อผิดพลาด"
            description="ไม่สามารถโหลดข้อมูลแดชบอร์ดได้"
            action={{
              label: "ลองใหม่",
              onClick: handleRefresh
            }}
          />
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div>
        {/* Page Header - Clean & Professional */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
              <RiDashboardLine className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-secondary-900 leading-tight">แดชบอร์ด</h1>
              <p className="text-xs text-secondary-500 mt-0.5">ภาพรวมระบบจัดการการเช่าอุปกรณ์</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline-flex items-center text-xs text-secondary-700 bg-secondary-100 px-2.5 py-1.5 rounded-lg gap-1.5">
              <RiTimeLine className="w-3.5 h-3.5" />
              อัพเดท {lastRefresh.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}
            </span>
            <RefreshButton
              onClick={handleRefresh}
              loading={isLoading || refreshing}
            />
          </div>
        </div>
        
        {!DASHBOARD_WIDGET_PERMISSIONS.some(p => hasPermission(p)) && (
          <EmptyState
            icon={<RiDashboardLine className="w-16 h-16" />}
            title="ยังไม่มีข้อมูลภาพรวมสำหรับสิทธิ์ของคุณ"
            description="บัญชีนี้ใช้งานได้เฉพาะบางเมนู — เลือกเมนูที่ต้องการจากแถบด้านซ้าย หรือติดต่อผู้ดูแลระบบหากต้องการดูภาพรวมบนแดชบอร์ด"
          />
        )}

        {/* Stat Cards - เรียงตาม order จาก config */}
        {(() => {
          // Define all stat card configurations
          const allStatCards = [
            {
              key: 'stat_total_rentals',
              permission: 'rentals.view',
              defaultOrder: 1,
              props: {
                title: 'การเช่าทั้งหมด',
                value: metrics.totalRentals,
                // P-SA69-1: value เป็นยอด "สะสม" แต่ delta เป็นของ flow "การเช่าใหม่รายเดือน"
                // → ต้องติดป้ายให้ชัดว่าเทียบอะไร ไม่งั้นอ่านเหมือนยอดสะสมลดลง
                delta: {
                  label: 'การเช่าใหม่',
                  current: metrics.newRentalsThisMonth,
                  previous: metrics.newRentalsLastMonth,
                },
                icon: <RiFileListLine />,
                bgColor: 'bg-info-50',
                textColor: 'text-info-700',
                iconColor: 'bg-info-200 text-info-600',
                link: '/rentals'
              }
            },
            {
              key: 'stat_active_rentals',
              permission: 'rentals.view',
              defaultOrder: 2,
              props: {
                title: 'การเช่าที่ใช้งานอยู่',
                value: metrics.activeRentals,
                icon: <RiContractLine />,
                bgColor: 'bg-success-50',
                textColor: 'text-success-700',
                iconColor: 'bg-success-200 text-success-600',
                link: '/rentals/list?status=active'
              }
            },
            {
              key: 'stat_total_companies',
              permission: 'companies.view',
              defaultOrder: 3,
              props: {
                title: 'บริษัททั้งหมด',
                value: metrics.totalCompanies,
                // P-SA69-1: เคสที่รายงานมา — 106 บริษัท (สะสม) ติด ▼100% แดง เพราะเดือนนี้
                // ไม่มีลูกค้าใหม่ (0) เทียบเดือนก่อน (1) ทั้งที่ไม่มีบริษัทไหนหายไปสักราย
                delta: {
                  label: 'ลูกค้าใหม่',
                  current: metrics.newCompaniesThisMonth,
                  previous: metrics.newCompaniesLastMonth,
                },
                icon: <RiCustomerServiceLine />,
                bgColor: 'bg-info-50',
                textColor: 'text-info-700',
                iconColor: 'bg-info-200 text-info-600',
                // KD3 S2352: การ์ดนับเฉพาะบริษัท active (company_stats.total) · หน้ารายการ default แสดงทุกสถานะ
                // (dev 332 vs 321) → deep-link ตัวกรอง active ให้ตัวเลขที่ปลายทางตรงกับการ์ด (แบบเดียวกับการ์ดเช่า/อุปกรณ์)
                link: '/master-data/companies?status=active'
              }
            },
            {
              key: 'stat_total_devices',
              permission: 'devices.view',
              defaultOrder: 4,
              props: {
                title: 'อุปกรณ์ทั้งหมด',
                value: metrics.totalDevices,
                // ปิดช่องว่างของการบวกเลขบนจอ: การ์ดอื่นครอบ available/rented/maintenance/
                // damaged/issued/reserved แล้ว เหลือ lost+disposed ที่ไม่มีการ์ดของตัวเอง
                note:
                  metrics.lostDevices || metrics.disposedDevices
                    ? `รวมสูญหาย ${metrics.lostDevices} · จำหน่ายออก ${metrics.disposedDevices}`
                    : undefined,
                icon: <RiDeviceLine />,
                bgColor: 'bg-accent-50',
                textColor: 'text-accent-700',
                iconColor: 'bg-accent-200 text-accent-600',
                link: '/master-data/devices'
              }
            },
            {
              key: 'stat_available_devices',
              permission: 'devices.view',
              defaultOrder: 5,
              props: {
                title: 'อุปกรณ์พร้อมใช้งาน',
                value: metrics.availableDevices,
                icon: <RiToolsLine />,
                bgColor: 'bg-success-50',
                textColor: 'text-success-700',
                iconColor: 'bg-success-200 text-success-600',
                link: '/master-data/devices?status=available'
              }
            },
            {
              key: 'stat_maintenance_devices',
              permission: 'devices.view',
              defaultOrder: 6,
              props: {
                title: 'อุปกรณ์ซ่อมบำรุง',
                value: metrics.maintenanceDevices,
                note: metrics.openMaintenanceJobs
                  ? `มีใบซ่อมค้าง ${metrics.openMaintenanceJobs} เครื่อง`
                  : undefined,
                icon: <RiToolsLine />,
                bgColor: 'bg-danger-50',
                textColor: 'text-danger-700',
                iconColor: 'bg-danger-200 text-danger-600',
                link: maintenanceLink
              }
            },
            {
              key: 'stat_damaged_devices',
              permission: 'devices.view',
              // PO 2026-09-08: เดิมเป็น 6.5 ขณะที่ backend AVAILABLE_WIDGETS และหน้าตั้งค่า
              // เป็น 7.5 → หน้า "ตั้งค่าแดชบอร์ด" โชว์ลำดับหนึ่งแต่จอจริงเรียงอีกแบบ (หน้าตั้งค่าโกหก)
              // guard D5 เทียบแค่ key+ชื่อ ไม่เทียบลำดับ จึงไม่เคยจับได้
              defaultOrder: 7.5,
              props: {
                title: 'อุปกรณ์ชำรุด/เสียหาย',
                value: metrics.damagedDevices,
                icon: <RiToolsLine />,
                bgColor: 'bg-warning-50',
                textColor: 'text-warning-700',
                iconColor: 'bg-warning-200 text-warning-600',
                link: '/master-data/devices?status=damaged'
              }
            },
            {
              key: 'stat_rented_devices',
              permission: 'devices.view',
              defaultOrder: 7,
              props: {
                title: 'อุปกรณ์กำลังเช่าใช้',
                value: metrics.rentedDevices,
                icon: <RiDeviceLine />,
                bgColor: 'bg-info-50',
                textColor: 'text-info-700',
                iconColor: 'bg-info-200 text-info-600',
                link: '/master-data/devices?status=rented'
              }
            },
            {
              // PO 2026-09-08: ของที่ถูกกันไว้ให้ใบงานเตรียมเครื่อง — ถ้าไม่มีการ์ดนี้ เครื่องจะ
              // หายจากทุกการ์ดโดยไม่มีใครรู้ว่าไปอยู่ไหน (ปัญหาเดียวกับที่ฟีเจอร์นี้ตั้งใจแก้)
              // กดแล้วไปแท็บ "ของค้างจอง" เพื่อให้ตามต่อได้ว่าค้างอยู่ใบไหน กี่วันแล้ว
              key: 'stat_reserved_devices',
              permission: 'devices.view',
              defaultOrder: 7.3,
              props: {
                title: 'จองไว้ (ใบงานเตรียมเครื่อง)',
                value: metrics.reservedDevices,
                icon: <RiDeviceLine />,
                bgColor: 'bg-accent-50',
                textColor: 'text-accent-700',
                iconColor: 'bg-accent-200 text-accent-600',
                link: reservedDevicesLink
              }
            },
            {
              // Stock Phase 2: เบิกไปใช้ภายใน — เหตุผลเดียวกับการ์ด "จองไว้"
              key: 'stat_issued_devices',
              permission: 'devices.view',
              defaultOrder: 7.6,
              props: {
                title: 'เบิกไปใช้ภายใน',
                value: metrics.issuedDevices,
                icon: <RiDeviceLine />,
                bgColor: 'bg-secondary-50',
                textColor: 'text-secondary-700',
                iconColor: 'bg-secondary-200 text-secondary-600',
                link: '/master-data/devices?status=issued'
              }
            },
            {
              key: 'stat_pending_cancel',
              permission: 'rentals.view',
              defaultOrder: 8,
              props: {
                title: 'รออนุมัติยกเลิก',
                value: metrics.pendingCancelRentals,
                icon: <RiFileListLine />,
                bgColor: 'bg-warning-50',
                textColor: 'text-warning-700',
                iconColor: 'bg-warning-200 text-warning-600',
                link: '/rentals/list?status=pending_cancellation'
              }
            },
            {
              key: 'stat_completed_rentals',
              permission: 'rentals.view',
              defaultOrder: 9,
              props: {
                title: 'เสร็จสิ้นแล้ว',
                value: metrics.completedRentals,
                icon: <RiFileListLine />,
                bgColor: 'bg-success-50',
                textColor: 'text-success-700',
                iconColor: 'bg-success-200 text-success-600',
                link: '/rentals/list?status=completed'
              }
            },
            {
              key: 'stat_total_contracts',
              permission: 'contracts.view',
              defaultOrder: 10,
              props: {
                title: 'สัญญาทั้งหมด',
                value: metrics.totalContracts,
                icon: <RiContractLine />,
                bgColor: 'bg-warning-50',
                textColor: 'text-warning-700',
                iconColor: 'bg-warning-200 text-warning-600',
                link: '/contracts'
              }
            },
            {
              key: 'stat_monthly_revenue',
              permission: 'reports.view',
              defaultOrder: 11,
              props: {
                title: 'รายได้เดือนนี้',
                value: formatCurrency(metrics.monthlyRevenue),
                // การ์ดนี้ value เป็น flow อยู่แล้ว (รายได้ "เดือนนี้") delta จึงตรงกับยอดบนการ์ด —
                // ใช้ StatDelta ตัวเดียวกันเพื่อให้ได้ guard 0-vs-N เหมือนการ์ดอื่น (เดิมเดือนก่อน
                // เป็น 0 จะโชว์ ▲0% เขียวเหมือนไม่มีอะไรเปลี่ยน ทั้งที่คิด % ไม่ได้)
                delta: {
                  label: 'รายได้',
                  current: metrics.monthlyRevenue,
                  previous: metrics.previousMonthRevenue,
                  format: formatCurrency,
                },
                icon: <RiMoneyDollarBoxLine />,
                bgColor: 'bg-success-50',
                textColor: 'text-success-700',
                iconColor: 'bg-success-200 text-success-600',
                link: '/reports/financial'
              }
            },
            {
              key: 'stat_utilization_rate',
              permission: 'reports.view',
              defaultOrder: 12,
              props: {
                title: 'อัตราการใช้งานอุปกรณ์',
                value: `${metrics.deviceUtilizationRate}%`,
                icon: <RiPieChartLine />,
                bgColor: 'bg-secondary-50',
                textColor: 'text-secondary-700',
                iconColor: 'bg-secondary-200 text-secondary-600',
                link: '/reports/devices'
              }
            }
          ];

          // Filter visible cards and sort by order
          // KD3 S2471: กรองสิทธิ์ก่อนแบ่งแถว — เดิมกรองแค่ config แล้วให้ PermissionGuard ซ่อนทีหลัง
          // แถวละ 4 เลยเว้นช่องโหว่ไม่เท่ากันเมื่อการ์ดกลางแถวไม่มีสิทธิ์
          const visibleCards = allStatCards
            .filter(card => isWidgetVisible(card.key) && hasPermission(card.permission))
            .map(card => ({
              ...card,
              order: getWidgetOrder(card.key, card.defaultOrder)
            }))
            .sort((a, b) => a.order - b.order);

          // Split into rows (4 cards per row for lg screens)
          const firstRow = visibleCards.slice(0, 4);
          const secondRow = visibleCards.slice(4);

          return (
            <>
              {firstRow.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {firstRow.map(card => (
                    <PermissionGuard key={card.key} requires={card.permission}>
                      <StatCard {...card.props} />
                    </PermissionGuard>
                  ))}
                </div>
              )}
              {secondRow.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  {secondRow.map(card => (
                    <PermissionGuard key={card.key} requires={card.permission}>
                      <StatCard {...card.props} />
                    </PermissionGuard>
                  ))}
                </div>
              )}
            </>
          );
        })()}
        
        {/* Charts Section with Icon Box Header - Dynamic Layout */}
        {(() => {
          const visibleCharts = [
            isWidgetVisible('chart_rental_trends') && 'chart_rental_trends',
            isWidgetVisible('chart_device_status') && 'chart_device_status'
          ].filter(Boolean);

          if (visibleCharts.length === 0) return null;

          // 1 widget = full width, 2 widgets = 2 columns
          const chartGridClass = visibleCharts.length === 1
            ? 'grid grid-cols-1 gap-6'
            : 'grid grid-cols-1 lg:grid-cols-2 gap-6';

          return (
            <PermissionGuard requires="reports.view">
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-4">
                  <RiLineChartLine className="w-4 h-4 text-secondary-400" />
                  <h2 className="text-sm font-semibold text-secondary-600">แนวโน้มและสถิติ</h2>
                  <div className="flex-1 h-px bg-secondary-200" />
                </div>

                <div className={chartGridClass}>
                  {isWidgetVisible('chart_rental_trends') && (
                    <RentalTrendChart data={rentalTrends} isLoading={isLoadingTrends} />
                  )}
                  {isWidgetVisible('chart_device_status') && (
                    <DeviceUtilizationChart data={deviceUtilization} isLoading={isLoadingCharts} />
                  )}
                </div>
              </div>
            </PermissionGuard>
          );
        })()}
        
        {/* Tables Section with Icon Box Header - Dynamic Auto-flow Layout */}
        {(() => {
          // Define all table widgets with their render functions
          const tableWidgetConfigs = [
            {
              key: 'table_top_companies',
              permission: 'companies.view',
              defaultOrder: 1,
              render: () => (
                <DataTable
                  title="บริษัทที่มีการเช่ามากที่สุด"
                  loadError={isTopCompaniesError}
                  data={topClients}
                  columns={[
                    { key: 'name', label: 'ชื่อบริษัท' },
                    { key: 'rental_count', label: 'จำนวนการเช่า' },
                    {
                      key: 'total_revenue',
                      label: 'รายได้รวม',
                      format: (value: number) => formatCurrency(value || 0)
                    }
                  ]}
                  action={{
                    label: 'ดูทั้งหมด',
                    link: '/master-data/companies'
                  }}
                />
              )
            },
            {
              key: 'table_companies_without_contracts',
              permission: 'contracts.view',
              defaultOrder: 2,
              render: () => (
                <DataTable
                  title="บริษัทที่ยังไม่มีสัญญา"
                  loadError={isCompaniesWithoutContractsError}
                  data={companiesWithoutContracts}
                  columns={[
                    { key: 'name', label: 'ชื่อบริษัท' },
                    { key: 'code', label: 'รหัสบริษัท' },
                    {
                      key: 'created_at',
                      label: 'วันที่เพิ่ม',
                      format: (value: string) => value ? formatDate(value) : '-'
                    }
                  ]}
                  action={canCreate('contracts') ? {
                    label: 'สร้างสัญญา',
                    link: '/contracts/create'
                  } : undefined}
                />
              )
            },
            {
              key: 'table_expiring_rentals',
              permission: 'rentals.view',
              defaultOrder: 3,
              render: () => (
                <DataTable
                  title="รายการเช่าที่ใกล้หมดอายุ"
                  loadError={isExpiringRentalsError}
                  data={expiringRentals}
                  columns={[
                    { key: 'rental_number', label: 'เลขที่เช่า' },
                    { key: 'company_name', label: 'บริษัท' },
                    {
                      key: 'days_remaining',
                      label: 'เหลือ',
                      format: (value: number | null) => formatDaysRemaining(value)
                    }
                  ]}
                  action={{
                    label: 'ดูทั้งหมด',
                    link: '/rentals/list?status=expiring'
                  }}
                />
              )
            },
            {
              key: 'table_expiring_contracts',
              permission: 'contracts.view',
              defaultOrder: 4,
              render: () => (
                <DataTable
                  title="สัญญาที่ใกล้หมดอายุ"
                  loadError={isExpiringContractsError}
                  data={expiringContracts}
                  columns={[
                    { key: 'contract_number', label: 'เลขที่สัญญา' },
                    { key: 'customer_name', label: 'ลูกค้า' },
                    {
                      key: 'days_remaining',
                      label: 'เหลือ',
                      format: (value: number | null) => formatDaysRemaining(value)
                    }
                  ]}
                  action={{
                    label: 'ดูทั้งหมด',
                    link: '/contracts/list?status=expiring'
                  }}
                />
              )
            },
            {
              key: 'table_recent_activities',
              // KD3 S2471: กิจกรรม = การเช่า/สัญญาที่สร้างใหม่ — backend กรองตามสิทธิ์รายชนิดแล้ว ไม่มีทั้งคู่ = ซ่อน
              permission: ['rentals.view', 'contracts.view'],
              defaultOrder: 5,
              render: () => (
                <div className="bg-white rounded-xl shadow-sm border border-secondary-100 overflow-hidden hover:shadow-md transition-shadow duration-300">
                  <div className="flex items-center justify-between px-5 py-3.5 border-b border-secondary-200 bg-gradient-to-r from-secondary-50/50 to-white">
                    <h2 className="text-sm font-semibold text-secondary-800">กิจกรรมล่าสุด</h2>
                    <span className="text-xs text-secondary-400 bg-secondary-100 px-2 py-0.5 rounded-full">
                      {recentActivities.length} รายการ
                    </span>
                  </div>
                  <div className="px-4 py-2">
                    {recentActivities.length > 0 ? (
                      <div className="divide-y divide-secondary-100">
                        {recentActivities.map((activity, index) => (
                          <div key={index} className="flex items-start gap-3 py-2.5">
                            <div className="flex-shrink-0 mt-0.5">
                              <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center">
                                <RiTimeLine className="w-3 h-3 text-primary-600" />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-secondary-800 leading-snug">
                                {activity.title || formatActivity(activity.type)}
                              </p>
                              {activity.description && (
                                <p className="text-xs text-secondary-500 mt-0.5 line-clamp-1">
                                  {activity.description}
                                </p>
                              )}
                              <p className="text-xs text-secondary-400 mt-0.5">
                                {formatDate(activity.timestamp)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : isRecentActivitiesError ? (
                      <EmptyState
                        icon={<RiTimeLine className="w-6 h-6" />}
                        title="โหลดข้อมูลไม่สำเร็จ"
                        description='ดึงกิจกรรมล่าสุดไม่ได้ — กดปุ่ม "รีเฟรช" ด้านบนเพื่อลองใหม่'
                        compact
                      />
                    ) : (
                      <EmptyState
                        icon={<RiTimeLine className="w-6 h-6" />}
                        title="ไม่มีกิจกรรม"
                        description="ยังไม่มีกิจกรรมล่าสุด"
                        compact
                      />
                    )}
                  </div>
                </div>
              )
            },
            {
              key: 'table_device_maintenance',
              permission: 'devices.view',
              defaultOrder: 6,
              render: () => (
                <DataTable
                  title="อุปกรณ์ที่ต้องซ่อม"
                  loadError={isMaintenanceDevicesError}
                  data={maintenanceDevices}
                  columns={[
                    { key: 'device_code', label: 'รหัส' },
                    { key: 'brand', label: 'ยี่ห้อ' },
                    { key: 'model', label: 'รุ่น' }
                  ]}
                  action={{
                    label: 'ดูทั้งหมด',
                    link: maintenanceLink
                  }}
                />
              )
            },
            {
              // user feedback Suraluck 2026-08-24 ข้อ 2: เคสซ่อมที่แจ้งเข้ามาใหม่ กดเข้าเคสได้จากแดชบอร์ด
              key: 'table_new_maintenance_cases',
              permission: 'maintenance.view',
              defaultOrder: 7,
              render: () => (
                <DataTable
                  title="เคสซ่อมใหม่ (สถานะเปิด)"
                  loadError={isNewMaintenanceCasesError}
                  data={newMaintenanceCases}
                  columns={[
                    {
                      key: 'case',
                      label: 'เคส',
                      format: (value: { id: number; no: string }) => (
                        <Link
                          to={`/maintenance/view/${value.id}`}
                          className="font-medium text-primary-600 hover:text-primary-700 hover:underline"
                        >
                          {value.no}
                        </Link>
                      )
                    },
                    { key: 'site_label', label: 'โครงการ' },
                    {
                      key: 'maintenance_type',
                      label: 'ประเภท',
                      format: (value: string | null) => value || 'ยังไม่ระบุ'
                    },
                    {
                      key: 'reported_at',
                      label: 'แจ้งเมื่อ',
                      format: (value: string) => value ? formatDateTime(value) : '—'
                    }
                  ]}
                  action={{
                    label: 'ดูทั้งหมด',
                    link: '/maintenance/list?status=OPEN'
                  }}
                />
              )
            },
            {
              // ใบงานเตรียมเครื่อง (PO 2026-09-08): เซลเห็นงานที่ตัวเองส่งไปว่าถึงไหนแล้ว
              // Support เห็นคิวที่ยังไม่มีใครรับ — ทั้งคู่กดเข้าใบได้จากแดชบอร์ดเลย
              key: 'table_preparation_jobs',
              permission: 'preparation_jobs.view',
              defaultOrder: 8,
              render: () => (
                <DataTable
                  title="ใบงานเตรียมเครื่อง (รอเตรียม)"
                  loadError={isPreparationJobsError}
                  data={preparationJobRows}
                  columns={[
                    {
                      key: 'job',
                      label: 'เลขที่ใบ',
                      format: (value: { id: number; no: string }) => (
                        <Link
                          to={`/rentals/preparation-jobs/${value.id}`}
                          className="font-medium text-primary-600 hover:text-primary-700 hover:underline"
                        >
                          {value.no}
                        </Link>
                      )
                    },
                    { key: 'customer_label', label: 'ลูกค้า' },
                    {
                      key: 'needed_date',
                      label: 'ต้องการวันที่',
                      format: (value: string | null) => value ? formatDate(value) : '—'
                    },
                    {
                      key: 'requested_by_name',
                      label: 'เปิดโดย',
                      format: (value: string | null) => value || '—'
                    }
                  ]}
                  action={{
                    label: 'ดูทั้งหมด',
                    link: '/rentals/preparation-jobs?status=REQUESTED'
                  }}
                />
              )
            }
          ];

          // Filter visible widgets and sort by order
          // KD3 S2471: กรองสิทธิ์ตรงนี้ด้วย — เดิม role ที่ไม่มีสิทธิ์ตารางใดเลยเห็นหัวข้อ "รายละเอียดและกิจกรรม"
          // ลอยอยู่เหนือพื้นที่ว่าง และจำนวนคอลัมน์นับตารางที่ตัวเองมองไม่เห็น
          const visibleTableWidgets = tableWidgetConfigs
            .filter(widget => isWidgetVisible(widget.key) && hasPermission(widget.permission))
            .map(widget => ({
              ...widget,
              order: getWidgetOrder(widget.key, widget.defaultOrder)
            }))
            .sort((a, b) => a.order - b.order);

          if (visibleTableWidgets.length === 0) return null;

          // Dynamic grid class: 1 widget = full width, 2+ = 2 columns with auto-flow
          const tableGridClass = visibleTableWidgets.length === 1
            ? 'grid grid-cols-1 gap-6'
            : 'grid grid-cols-1 lg:grid-cols-2 gap-6';

          return (
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-4">
                <RiFileListLine className="w-4 h-4 text-secondary-400" />
                <h2 className="text-sm font-semibold text-secondary-600">รายละเอียดและกิจกรรม</h2>
                <div className="flex-1 h-px bg-secondary-200" />
              </div>

              <div className={tableGridClass}>
                {visibleTableWidgets.map(widget => (
                  <PermissionGuard key={widget.key} requires={widget.permission}>
                    {widget.render()}
                  </PermissionGuard>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    </Layout>
  );
};

export default DashboardPage;